"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";

export type QueryKeyPart = string | number | boolean | null | undefined;
export type QueryKey = readonly QueryKeyPart[];

type QueryNotificationReason = "state" | "invalidate" | "reset";

type QueryEntry<T = unknown> = {
  revision: number;
  request: number;
  data?: T;
  error?: unknown;
  updatedAt: number;
  promise: Promise<T> | null;
  listeners: Set<(reason: QueryNotificationReason) => void>;
};

export interface UseQueryResult<T> {
  data: T | undefined;
  error: unknown;
  isLoading: boolean;
  refetch: () => Promise<T | undefined>;
}

let resetGeneration = 0;
const queryEntries = new Map<string, QueryEntry<unknown>>();

function serializeKey(queryKey: QueryKey): string {
  return JSON.stringify(queryKey);
}

function matchesPrefix(queryKey: QueryKey, prefix: QueryKey): boolean {
  if (prefix.length > queryKey.length) return false;
  return prefix.every((part, index) => queryKey[index] === part);
}

function ensureEntry<T>(queryKey: QueryKey): QueryEntry<T> {
  const key = serializeKey(queryKey);
  const existing = queryEntries.get(key) as QueryEntry<T> | undefined;
  if (existing) return existing;
  const created: QueryEntry<T> = {
    revision: 0,
    request: 0,
    updatedAt: 0,
    promise: null,
    listeners: new Set(),
  };
  queryEntries.set(key, created as QueryEntry<unknown>);
  return created;
}

function notify(queryKey: QueryKey, reason: QueryNotificationReason = "state") {
  const entry = ensureEntry(queryKey);
  entry.revision += 1;
  entry.listeners.forEach((listener) => listener(reason));
}

function isFresh(queryKey: QueryKey, staleTime: number): boolean {
  const entry = ensureEntry(queryKey);
  return (
    entry.updatedAt > 0 &&
    performance.now() - entry.updatedAt >= 0 &&
    performance.now() - entry.updatedAt < staleTime
  );
}

export const queryClient = {
  async fetchQuery<T>({
    queryKey,
    queryFn,
    staleTime = 0,
    force = false,
  }: {
    queryKey: QueryKey;
    queryFn: () => Promise<T>;
    staleTime?: number;
    force?: boolean;
  }): Promise<T> {
    const entry = ensureEntry<T>(queryKey);
    if (!force && entry.data !== undefined && isFresh(queryKey, staleTime)) {
      return entry.data;
    }
    if (entry.promise) {
      return entry.promise;
    }

    const request = ++entry.request;
    const current = () =>
      entry.request === request &&
      queryEntries.get(serializeKey(queryKey)) === entry;
    entry.promise = queryFn()
      .then((result) => {
        if (!current()) throw new Error("QUERY_CONTEXT_CHANGED");
        entry.data = result;
        entry.error = undefined;
        entry.updatedAt = performance.now();
        entry.promise = null;
        notify(queryKey);
        return result;
      })
      .catch((error) => {
        if (!current()) throw error;
        entry.error = error;
        entry.promise = null;
        notify(queryKey);
        throw error;
      });

    return entry.promise;
  },

  invalidateQueries(
    prefix: QueryKey,
    options?: { exclude?: QueryKey[]; notify?: boolean },
  ): void {
    const excluded = new Set(
      (options?.exclude ?? []).map((queryKey) => serializeKey(queryKey)),
    );
    for (const [serializedKey, entry] of queryEntries.entries()) {
      if (excluded.has(serializedKey)) continue;
      const queryKey = JSON.parse(serializedKey) as QueryKey;
      if (!matchesPrefix(queryKey, prefix)) continue;
      entry.request += 1;
      entry.updatedAt = 0;
      entry.promise = null;
      if (options?.notify !== false) {
        notify(queryKey, "invalidate");
      }
    }
  },

  setQueryData<T>(
    queryKey: QueryKey,
    updater: T | ((current: T | undefined) => T | undefined),
  ): void {
    const entry = ensureEntry<T>(queryKey);
    entry.request += 1;
    const nextValue =
      typeof updater === "function"
        ? (updater as (current: T | undefined) => T | undefined)(entry.data)
        : updater;
    if (nextValue === undefined) {
      delete entry.data;
    } else {
      entry.data = nextValue;
    }
    entry.error = undefined;
    entry.updatedAt = nextValue === undefined ? 0 : performance.now();
    entry.promise = null;
    notify(queryKey);
  },

  subscribe(
    queryKey: QueryKey,
    listener: (reason: QueryNotificationReason) => void,
  ): () => void {
    const entry = ensureEntry(queryKey);
    entry.listeners.add(listener);
    return () => {
      entry.listeners.delete(listener);
    };
  },

  getSnapshot<T>(queryKey: QueryKey): QueryEntry<T> {
    return ensureEntry<T>(queryKey);
  },

  isFresh,

  getResetGeneration: () => resetGeneration,

  clear(): void {
    resetGeneration += 1;
    for (const [serialized, entry] of queryEntries) {
      entry.request += 1;
      entry.data = undefined;
      entry.error = undefined;
      entry.promise = null;
      entry.updatedAt = 0;
      notify(JSON.parse(serialized) as QueryKey, "reset");
    }
  },
};

export function useQuery<T>({
  queryKey,
  queryFn,
  enabled = true,
  staleTime = 0,
}: {
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  enabled?: boolean;
  staleTime?: number;
}): UseQueryResult<T> {
  const keyString = useMemo(() => serializeKey(queryKey), [queryKey]);
  const key = useMemo(() => JSON.parse(keyString) as QueryKey, [keyString]);
  const queryFnRef = useRef(queryFn);
  useEffect(() => {
    queryFnRef.current = queryFn;
  }, [queryFn]);
  const load = useCallback(
    async (force = false) => {
      if (!enabled) return undefined;
      try {
        return await queryClient.fetchQuery<T>({
          queryKey: key,
          queryFn: () => queryFnRef.current(),
          staleTime,
          force,
        });
      } catch {
        return undefined;
      }
    },
    [enabled, key, staleTime],
  );
  const subscribe = useCallback(
    (changed: () => void) =>
      queryClient.subscribe(key, (reason) => {
        changed();
        if (reason === "invalidate" && enabled) void load(true);
      }),
    [key, enabled, load],
  );
  const revision = useCallback(
    () => queryClient.getSnapshot<T>(key).revision,
    [key],
  );
  useSyncExternalStore(subscribe, revision, revision);
  const generation = queryClient.getResetGeneration();
  useEffect(() => {
    if (enabled && !queryClient.isFresh(key, staleTime)) void load();
  }, [enabled, key, staleTime, load, generation]);
  // Read the entry for this render's key, never local state from a previous key.
  const entry = queryClient.getSnapshot<T>(key);
  const refetch = useCallback(() => load(true), [load]);
  return {
    data: enabled ? entry.data : undefined,
    error: enabled ? entry.error : undefined,
    isLoading: enabled && entry.data === undefined && entry.error === undefined,
    refetch,
  };
}
